import clsx from 'clsx'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'
import React from 'react'
import { LinkType } from '../../generated/content'
import type { DiscussionPostResult } from '../data/fragments/DiscussionPostFragment'
import type { PlantProfileResult } from '../data/fragments/PlantProfileFragment'
import { useIsSignedIn } from '../hooks/useAuth'
import useMediaQuery from '../hooks/useMediaQuery'
import { usePlanteeUser } from '../hooks/usePlanteeUser'
import { filterNonEmpty } from '../utils/filterNonEmpty'
import { AlertPopup } from './AlertPopup'
import { Box } from './Box'
import { Button } from './Buttons'
import { CardPlant } from './CardPlant'
import { Comments } from './Comments'
import { Container } from './Container'
import { CustomUpload } from './CustomUpload'
import { Flex } from './Flex'
import { Icon } from './Icon'
import { Likes } from './Likes'
import styles from './PlantProfile.module.sass'
import { TextArea } from './TextArea'
import { UserBadge } from './UserBadge'

function ProfileBtns(props: { isMobile?: boolean; profileId: string }) {
	const locale = useRouter().locale
	return (
		<Flex
			marginTop={props.isMobile ? 40 : 0}
			spacing={20}
			justifyContent={props.isMobile ? 'flex-start' : 'flex-end'}>
			<Button
				style={{ marginBottom: '10px' }}
				type="link"
				isBorder
				href={`/create?id=${props.profileId}`}
				icon={{ name: 'duplicate', position: 'start' }}>
				{locale === 'cs' ? 'Duplikovat a upravit' : 'Duplicate and edit'}
			</Button>
			<Button
				href="https://plant.ee/products/plantee"
				type="link"
				target="_blank"
				fill="green600"
				icon={{ name: 'plant', position: 'end' }}>
				{locale === 'cs' ? 'Pěstovat v Plantee' : 'Grow me in Plantee'}
			</Button>
		</Flex>
	)
}

export function PlantProfile(props: {
	profile: PlantProfileResult
	authorProfiles: PlantProfileResult[]
	discussion: DiscussionPostResult[]
}) {
	const isMobile = useMediaQuery('(max-width: 768px)')
	const [activeImageIndex, setActiveImageIndex] = React.useState<number>(0)
	const router = useRouter()
	const commentInput = React.useRef(null)
	const [commentImage, setCommentImage] = React.useState<File | undefined>(undefined)
	const plantee = usePlanteeUser()
	const [comments, setComments] = React.useState<DiscussionPostResult[]>(props.discussion)
	const [phasesPopupOpen, setPhasesPopupOpen] = React.useState(false)
	const signedIn = useIsSignedIn()
	const locale = router.locale

	const images = props.profile.activeRevision?.images?.items.sort(
		(a, b) => Number(b.image?.isThumbnail) - Number(a.image?.isThumbnail)
	)

	return (
		<div className={clsx(styles.wrapper)}>
			<button
				className={styles.close}
				onClick={() => {
					router.back()
				}}>
				<Icon name="close"></Icon>
			</button>
			<div className={styles.inner}>
				<Container view="wide">
					<div className={styles.head}>
						<div className={styles.left}>
							<h1>
								{props.profile.activeRevision?.localesByLocale?.name}
								<br></br>
								<span>{props.profile.activeRevision?.latinName}</span>
							</h1>
							<div className={styles.gallery}>
								<div className={styles.galleryMain}>
									{images?.map((item, index) => {
										if (item.image) {
											return (
												<div
													key={index}
													className={clsx(
														styles.galleryMainImage,
														index === activeImageIndex && styles.active
													)}>
													<Image
														key={String(item.image?.id)}
														src={item.image?.url}
														layout="fill"
														objectFit="cover"
														alt={item.image.alt ?? item.image.description ?? ''}
														placeholder="blur"
														blurDataURL={item.image.url}
													/>
												</div>
											)
										}
									})}
								</div>
								<div className={styles.gallerySide}>
									{images?.map((item, index) => {
										if (item.image) {
											return (
												<button
													key={index}
													className={styles.gallerySideImage}
													onClick={() => {
														setActiveImageIndex(index)
													}}>
													<Image
														key={String(item.image?.id)}
														src={item.image?.url}
														layout="fill"
														objectFit="cover"
														alt={item.image.alt ?? item.image.description ?? ''}
														placeholder="blur"
														blurDataURL={item.image.url}
													/>
												</button>
											)
										}
									})}
								</div>
							</div>
						</div>
						<div className={styles.right}>
							{!isMobile && <ProfileBtns profileId={props.profile.id} />}
							<Flex marginTop={55} justifyContent="space-between">
								<div className={styles.author}>
									<span>By</span>
									<UserBadge
										name={props.profile.author?.nick ?? ''}
										image={props.profile.author?.avatar}
										isHighlighted
										link={{
											internalLink: { url: `/author/${props.profile.author?.id}` },
											type: LinkType.internal,
										}}
									/>
								</div>
								<div className={styles.likes}>
									<Likes count={props.profile.likedByUser.length} plantProfile={props.profile} />
								</div>
							</Flex>
							<article className={styles.description}>
								{props.profile.activeRevision?.localesByLocale?.description}
							</article>
							{props.profile.activeRevision?.localesByLocale?.growingConditions && (
								<article className={styles.description}>
									<h3>Growing conditions</h3>
									{props.profile.activeRevision?.localesByLocale?.growingConditions}
								</article>
							)}
							{isMobile && <ProfileBtns profileId={props.profile.id} isMobile />}
						</div>
					</div>
					<div className={styles.phases}>
						<h2>
							{locale === 'cs' ? 'Fáze růstu' : 'Growing phases'} (
							{props.profile.activeRevision?.phases.length})
						</h2>
						<Flex marginTop={50} marginBottom={50} spacing={50} justifyContent="flex-start">
							{props.profile.activeRevision?.phases.map((item, index) => {
								return (
									<div className={styles.phase} key={index}>
										{item.phaseType?.icon && (
											<div className={styles.phaseImage}>
												<Image
													key={String(item.phaseType?.icon?.id)}
													src={item.phaseType?.icon?.url}
													layout="responsive"
													objectFit="cover"
													width={item.phaseType.icon.width}
													height={item.phaseType.icon.height}
													alt={item.phaseType?.icon.alt ?? item.phaseType?.icon.description ?? ''}
													placeholder="blur"
													blurDataURL={item.phaseType?.icon.url}
												/>
											</div>
										)}
										<h4>{item.phaseType?.localesByLocale?.name}</h4>
									</div>
								)
							})}
						</Flex>
						<Button
							type="button"
							fill="grey100"
							onClick={() => {
								setPhasesPopupOpen(true)
							}}>
							{locale === 'cs' ? 'Více o fázích' : 'More about phases'}
						</Button>
						<AlertPopup
							isOpen={phasesPopupOpen}
							onClickClose={() => {
								setPhasesPopupOpen(false)
							}}
							type="default">
							<div className={styles.phasesPopup}>
								<h2>
									{locale === 'cs' ? 'Fáze růstu' : 'Growing phases'} (
									{props.profile.activeRevision?.phases.length})
								</h2>
								{props.profile.activeRevision?.phases.map((phase, index) => {
									return (
										<div key={index}>
											<Flex justifyContent="space-between" marginBottom={20}>
												<Flex>
													{phase.phaseType?.icon && (
														<div className={styles.phasesPopupIcon}>
															<Image
																key={String(phase.phaseType?.icon?.id)}
																src={phase.phaseType?.icon?.url}
																layout="responsive"
																objectFit="cover"
																width={phase.phaseType.icon.width}
																height={phase.phaseType.icon.height}
																alt={
																	phase.phaseType?.icon.alt ??
																	phase.phaseType?.icon.description ??
																	''
																}
																placeholder="blur"
																blurDataURL={phase.phaseType?.icon.url}
															/>
														</div>
													)}
													<h3>{`${index + 1}. ${phase.phaseType?.localesByLocale?.name}`}</h3>
												</Flex>
												<p>{phase.daysUntilNextPhase} days</p>
											</Flex>
											<ul className={styles.phasesPopupList}>
												<li>
													<Icon name="sun" />
													<Flex justifyContent="space-between" alignItems="center">
														<h4>{locale === 'cs' ? 'Světlo' : 'Lighting'}</h4>
														<p>
															{phase.monday?.ppfd}/m2/s, {phase.monday?.dli}/mol/m2d
														</p>
													</Flex>
												</li>
												<li>
													<Icon name="temperature" />
													<Flex justifyContent="space-between" alignItems="center">
														<h4>{locale === 'cs' ? 'Teplota půdy' : 'Soil temperature'}</h4>
														<p>{phase.monday?.daySoilTemperature}°C</p>
													</Flex>
												</li>
												<li>
													<Icon name="mositure" />
													<Flex justifyContent="space-between" alignItems="center">
														<h4>{locale === 'cs' ? 'Vlhost půdy' : 'Soil moisture'}</h4>
														<p>{phase.monday?.startWatering}%</p>
													</Flex>
												</li>
												<li>
													<Icon name="planttemprature" />
													<Flex justifyContent="space-between" alignItems="center">
														<h4>{locale === 'cs' ? 'Teplota rostliny' : 'Plant temperature'}</h4>
														<p>{phase.monday?.dayPlantTemperature}°C</p>
													</Flex>
												</li>
												<li>
													<Icon name="airhumidity" />
													<Flex justifyContent="space-between" alignItems="center">
														<h4>{locale === 'cs' ? 'Vlhkost vzduchu' : 'Air humidity'}</h4>
														<p>{phase.monday?.dayPlantTemperature}%</p>
													</Flex>
												</li>
											</ul>
										</div>
									)
								})}
							</div>
						</AlertPopup>
					</div>
					<div className={styles.discussion}>
						<h2>{locale === 'cs' ? 'Komentáře' : 'Comments'}</h2>
						<Comments comments={comments} />
						{signedIn ? (
							<form
								className={styles.commentForm}
								onSubmit={(e) => {
									e.preventDefault()
									console.log('comment submit')
									const textarea = commentInput.current
										? (commentInput.current as HTMLTextAreaElement)
										: null
									const comment = textarea ? textarea.value : null
									if (comment) {
										plantee.auth.addComment
											.mutateAsync({
												text: comment,
												plantProfileId: String(props.profile.id),
												image: commentImage,
												mimeType: commentImage ? commentImage.type : undefined,
											})
											.then((v) => {
												if (v.mutation.createDiscussionPost.ok && textarea) {
													textarea.value = ''
												}
												if (v.comments.listDiscussionPost) {
													setComments(v.comments.listDiscussionPost)
												}
											})
									}
								}}>
								<h2>{locale === 'cs' ? 'Přidej komentář' : 'Add your own comment'}</h2>
								<Box>
									<TextArea
										ref={commentInput}
										required
										name="comment"
										noBorder
										placeholder={
											locale === 'cs'
												? 'Pěstovali jste tuto rostlinu? Dej tvůrci zpětnou vazbu a ukaž ostatním tvoje výsledky.'
												: 'Did you tried this plant profile? Give the creator your feedback and others people your results. It helps the community grows!'
										}
									/>
									<div className={styles.commentFormBtns}>
										<CustomUpload
											accept={{
												'image/*': [],
											}}
											small
											maxSize={10000000}
											iconDecoration="imageIllustration"
											areaDescription="Drop your image here"
											footerDescription={<>Supported files: jpg, png. Max 10 MB.</>}
											onDropAccepted={async ([file]) => {
												console.log(file)
												if (file) {
													setCommentImage(file)
												}
											}}
										/>
										<div className={styles.commentSubmit}>
											{plantee.auth.addComment.data?.mutation.createDiscussionPost.ok ? (
												<p>
													{locale === 'cs'
														? 'Komentář úspěšně přidán'
														: 'Comment added sucessfully'}
												</p>
											) : (
												<Button type="submit" fill="groundBlack">
													{locale === 'cs' ? 'Zveřejnit komentář' : 'Submit comment'}
												</Button>
											)}
										</div>
									</div>
								</Box>
							</form>
						) : (
							<Button type="link" fill="green600" href={'/user/edit'}>
								{locale === 'cs' ? 'Přihlásit se pro přidání komentáře' : 'Log in to comment'}
							</Button>
						)}
					</div>
					<div className={styles.otherProfiles}>
						<h2>
							{locale === 'cs' ? 'Více pěstebních profilů od uživatele' : 'More profiles from user'}
						</h2>
						<Flex
							spacing={isMobile ? 10 : 40}
							itemsPerRow={5}
							mobileItemsPerRow={2}
							marginTop={30}
							marginBottom={30}>
							{props.authorProfiles.map((item, index) => {
								if (item.id !== props.profile.id) {
									return (
										<CardPlant
											key={index}
											images={
												item.activeRevision?.images?.items
													.map((item) => item.image)
													.filter(filterNonEmpty) ?? []
											}
											title={item.activeRevision?.localesByLocale?.name ?? ''}
											user={{
												name: (item.author?.nick || item.author?.email) ?? '',
												image: item.author?.avatar,
												id: String(item.author?.id),
											}}
											likes={0}
											link={`/profile/${item.author?.id}/${item.id}`}
											plantProfile={item}
										/>
									)
								}
							})}
						</Flex>
						<Button type="link" href={`/author/${props.profile.author?.id}`} fill="grey100">
							{locale === 'cs' ? 'Zobrazit vše' : 'View all'}
						</Button>
					</div>
				</Container>
			</div>
		</div>
	)
}
